import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Breaker, Button, Flexbox, InputField, LabelInput } from "../../Theme/GlobalStyles";
import { Text } from "../../Theme/Typography";
import { CloseCircle } from "../../Theme/Icons";
import { globalTheme } from "../../Theme/Theme";
import { PopupBody, PopupWrapper } from "./CommonPopup";

// react-select styling so the multi-selects match the themed inputs: soft
// nuetral pill control, blue badge chips, clean indicators + rounded menu.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 24,
    border: `1px solid ${state.isFocused ? globalTheme.colors.grey4 : globalTheme.colors.nuetral2}`,
    background: globalTheme.colors.nuetral2,
    boxShadow: "none",
    padding: "3px 8px",
    ":hover": { border: `1px solid ${globalTheme.colors.grey4}` },
  }),
  valueContainer: (base) => ({ ...base, gap: 4, padding: "2px 4px" }),
  placeholder: (base) => ({ ...base, color: globalTheme.colors.grey3, fontSize: 14 }),
  input: (base) => ({ ...base, color: globalTheme.colors.black4, fontSize: 14 }),
  multiValue: (base) => ({
    ...base,
    borderRadius: 20,
    background: globalTheme.colors.blue,
    padding: "1px 4px",
    overflow: "hidden",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: globalTheme.colors.blue2,
    fontSize: 12,
    fontWeight: 500,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: globalTheme.colors.blue2,
    borderRadius: 20,
    ":hover": { background: globalTheme.colors.blue2, color: globalTheme.colors.white1 },
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({
    ...base,
    color: globalTheme.colors.nuetral6,
    ":hover": { color: globalTheme.colors.nuetral8 },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: globalTheme.colors.nuetral6,
    cursor: "pointer",
    ":hover": { color: globalTheme.colors.red },
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: 8,
    fontSize: 14,
    cursor: "pointer",
    color: globalTheme.colors.black4,
    background: state.isSelected
      ? globalTheme.colors.blue
      : state.isFocused
      ? globalTheme.colors.nuetral2
      : "transparent",
    ":active": { background: globalTheme.colors.nuetral2 },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    overflow: "hidden",
    zIndex: 9999,
    boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.12)",
  }),
  menuList: (base) => ({ ...base, padding: 6 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const CreateUserPopup = ({
  isActive,
  onClickCreateUser,
  onClickClosePopup,
  onChangeFormData,
  errors = {},
  isCreateUserPopupFetching,
  dropdownOptions = [],
  programList = [],
  setRoleIds,
  setProgramIds,
}) => {
  const [roleSelection, setRoleSelection] = useState([]);
  const [programSelection, setProgramSelection] = useState([]);

  // Reset the local multi-select state every time the popup opens.
  useEffect(() => {
    if (!isActive) return;
    setRoleSelection([]);
    setProgramSelection([]);
  }, [isActive]);

  const roleOptionArray = useMemo(
    () =>
      (dropdownOptions || []).map((element) => ({
        value: element.id,
        label: element.roleName,
      })),
    [dropdownOptions]
  );

  const programOptionArray = useMemo(
    () =>
      (programList || []).map((element) => ({
        value: element.batchId,
        label: element.programName,
      })),
    [programList]
  );

  return (
    <PopupWrapper className={`${isActive ? "active" : ""}`}>
      <PopupBody maxWidth={560} maxHeight={640}>
        <Flexbox spaceBW alignCenter>
          <Text font18 inter semiBold white maxWidth="max-content">
            Create New User
          </Text>
          <CloseCircle className={"pointer"} onClick={onClickClosePopup} />
        </Flexbox>

        <Breaker marginTop={16} marginBottom={20} borderColor={globalTheme.colors.white1} />

        <Flexbox column gap={28}>
          <InputField
            name="firstName"
            label="First Name"
            isImp
            placeholder="Enter First Name"
            onChange={onChangeFormData}
            error={errors.firstName}
          />
          <InputField
            name="lastName"
            label="Last Name"
            placeholder="Enter Last Name"
            onChange={onChangeFormData}
          />
          <InputField
            name="uid"
            label="UID"
            placeholder="Enter UID"
            onChange={onChangeFormData}
          />
          <InputField
            name="mobileNumber"
            label="Phone No"
            isImp
            placeholder="Enter Phone No"
            onChange={onChangeFormData}
            error={errors.mobileNumber}
          />
          <InputField
            name="email"
            label="Email ID"
            isImp
            placeholder="Enter Email ID"
            onChange={onChangeFormData}
            error={errors.email}
          />
          <InputField
            name="alternateEmail"
            label="Alternate Email"
            placeholder="Enter Alternate Email"
            onChange={onChangeFormData}
          />

          <div>
            <LabelInput>Assign Program</LabelInput>
            <Select
              isMulti
              options={programOptionArray}
              value={programSelection}
              styles={selectStyles}
              placeholder="Select Program"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPlacement="auto"
              onChange={(selected) => {
                const list = selected || [];
                setProgramSelection(list);
                setProgramIds(list);
              }}
            />
          </div>

          <div>
            <LabelInput>Select Roles</LabelInput>
            <Select
              isMulti
              options={roleOptionArray}
              value={roleSelection}
              styles={selectStyles}
              placeholder="Select Roles"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPlacement="auto"
              onChange={(selected) => {
                const list = selected || [];
                setRoleSelection(list);
                setRoleIds(list);
              }}
            />
          </div>
        </Flexbox>

        <Flexbox alignCenter gap={12} justiCenter className="mt20">
          <Button
            buttonMd
            whiteFilled
            name="Cancel"
            disabled={isCreateUserPopupFetching}
            onClick={onClickClosePopup}
          />
          <Button
            buttonMd
            primaryFilled
            name="Submit"
            isCalling={isCreateUserPopupFetching}
            disabled={isCreateUserPopupFetching}
            onClick={onClickCreateUser}
          />
        </Flexbox>
      </PopupBody>
    </PopupWrapper>
  );
};

export default CreateUserPopup;
